import { useCampaignSendSizeData } from 'modules/campaigns/lib/hooks';
import { AUDIENCE_BREAKDOWN_FORM_FIELDS } from 'modules/experiments/constants/audienceBreakdown';
import { useVariantConfigurationContext } from 'modules/experiments/contexts';
import { getTakeoverCampaignId } from 'modules/experiments/ducks/selectors';
import { AudienceBreakdownMetric } from 'modules/experiments/types';
import {
  getAudienceBreakdownMetricsBySection,
  getHasError,
  getTestGroupSize,
  hasOptimizedGroup,
} from 'modules/experiments/utils';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { AudienceBreakdownChartSection } from './AudienceBreakdownChartSection';
import { AudienceBreakdownChartWrapper } from './Styled';

export const AudienceBreakdownChart = () => {
  // Use Form Context
  const {
    formState: { errors },
  } = useFormContext();

  const [hasHoldoutGroup, holdoutGroupSize, testStrategy, testGroupSize] = useWatch({
    name: AUDIENCE_BREAKDOWN_FORM_FIELDS,
  });

  // Chart State
  const [hasHoldoutGroupChartState, setHasHoldoutGroupChartState] = useState(hasHoldoutGroup);
  const [holdoutGroupSizeChartState, setHoldoutGroupSizeChartState] = useState(holdoutGroupSize);
  const [hasOptimizedGroupChartState, setHasOptimizedGroupChartState] = useState(
    hasOptimizedGroup(testStrategy),
  );
  const [testGroupSizeChartState, setTestGroupSizeChartState] = useState(
    getTestGroupSize({
      testStrategy,
      testGroupSize,
      hasHoldoutGroup,
      holdoutGroupSize,
    }),
  );
  const [breakdownMetricState, setBreakdownMetricState] = useState<AudienceBreakdownMetric[]>([]);

  const campaignId = useSelector(getTakeoverCampaignId);
  // Campaign State
  const {
    estimatedSendSizeData: { sendSize },
  } = useCampaignSendSizeData(campaignId, 0);
  const hasSendSize = !!sendSize;

  // Variant State
  const { variantList } = useVariantConfigurationContext();

  // Effects
  useEffect(() => {
    if (!variantList.length || getHasError(AUDIENCE_BREAKDOWN_FORM_FIELDS, errors)) return;

    setHasHoldoutGroupChartState(hasHoldoutGroup);
    setHoldoutGroupSizeChartState(holdoutGroupSize);
    setHasOptimizedGroupChartState(hasOptimizedGroup(testStrategy));
    setTestGroupSizeChartState(
      getTestGroupSize({
        testStrategy,
        testGroupSize,
        hasHoldoutGroup,
        holdoutGroupSize,
      }),
    );
  }, [hasHoldoutGroup, holdoutGroupSize, testStrategy, testGroupSize, errors]);

  useEffect(() => {
    if (!variantList.length || getHasError(AUDIENCE_BREAKDOWN_FORM_FIELDS, errors)) return;

    const breakdownMetrics = getAudienceBreakdownMetricsBySection({
      sendSize,
      hasHoldoutGroup: hasHoldoutGroupChartState,
      holdoutGroupPercent: holdoutGroupSizeChartState,
      hasOptimizedGroup: hasOptimizedGroupChartState,
      testGroupPercent: testGroupSizeChartState,
      variantList,
    });
    setBreakdownMetricState(breakdownMetrics);
  }, [
    hasHoldoutGroupChartState,
    holdoutGroupSizeChartState,
    hasOptimizedGroupChartState,
    testGroupSizeChartState,
    errors,
    variantList.length,
  ]);

  // should never happen because we always have a control variant
  if (!variantList.length) return <div data-test="audience-breakdown-chart-empty" />;

  return (
    <AudienceBreakdownChartWrapper data-test="audience-breakdown-chart-wrapper">
      {breakdownMetricState.map((value, index) => {
        const isFirstSection = index === 0;
        const isLastSection = index === breakdownMetricState.length - 1;

        return (
          <AudienceBreakdownChartSection
            key={index}
            chartData={value}
            index={index}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
            hasSendSize={hasSendSize}
            data-test="audience-breakdown-chart-section"
          />
        );
      })}
    </AudienceBreakdownChartWrapper>
  );
};
